/**
 * useNavigationHistory — Tracks the last visited routes (deduped, max 20).
 * Persists to sessionStorage so it survives reloads in the same tab.
 *
 * - Stores only `location.pathname` (ignores query/hash).
 * - Excludes technical routes (/auth, /onboarding, /profile-setup).
 * - Memory store is shared between hook instances via module-level
 *   subscription, so all consumers stay in sync.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "perform.nav.history";
const MAX_ENTRIES = 20;

const EXCLUDED_PREFIXES = ["/auth", "/onboarding", "/profile-setup"];

export interface NavHistoryEntry {
  pathname: string;
  visitedAt: number;
}

function loadHistory(): NavHistoryEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e: any): e is NavHistoryEntry =>
        e && typeof e.pathname === "string" && typeof e.visitedAt === "number"
    );
  } catch {
    return [];
  }
}

function saveHistory(entries: NavHistoryEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* quota / disabled — ignore */
  }
}

let memoryHistory: NavHistoryEntry[] | null = null;
const listeners = new Set<(h: NavHistoryEntry[]) => void>();

function getHistory(): NavHistoryEntry[] {
  if (memoryHistory === null) memoryHistory = loadHistory();
  return memoryHistory;
}

function setHistory(next: NavHistoryEntry[]) {
  memoryHistory = next;
  saveHistory(next);
  listeners.forEach((fn) => fn(next));
}

function isExcluded(path: string): boolean {
  if (!path || path === "/") return true;
  return EXCLUDED_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
}

export function useNavigationHistory() {
  const location = useLocation();
  const [history, setLocalHistory] = useState<NavHistoryEntry[]>(getHistory);

  useEffect(() => {
    const fn = (h: NavHistoryEntry[]) => setLocalHistory(h);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (isExcluded(path)) return;

    const current = getHistory();
    const top = current[0];
    if (top && top.pathname === path) return;

    const filtered = current.filter((e) => e.pathname !== path);
    const next = [
      { pathname: path, visitedAt: Date.now() },
      ...filtered,
    ].slice(0, MAX_ENTRIES);
    setHistory(next);
  }, [location.pathname]);

  return {
    history,
    currentPath: location.pathname,
  };
}
